import React, { useEffect, useState, useContext, createContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import {
	Button,
	ContentContainer,
	TitleHeader,
	TableLoading,
	AlertMessage,
	NarrowContentWrapper,
} from "../components/UiComponents";
import {
	CourseHeader,
	CourseWrapper,
} from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";

const AssessmentContent = createContext(null);

const multipleChoiceQuestions = {
	reasonable_force: {
		a: "A. Proportionate, Reasoning, Never",
		b: "B. Prompting, Reason, Necessary",
		c: "C. Personal, Reasonable, Nuclear",
		d: "D. Proportionate, Reasonable, Necessary",
	},
	head_person_role: {
		a: "A. Care, Compassion, Capable",
		b: "B. Compatible, Care Control",
		c: "C. Care, Control, Communication",
		d: "D. Communication, Care, Compassion",
	},
	breakaway: {
		a: "A. Speed, Skill, Safety",
		b: "B. Safety, Surprise, Speed",
		c: "C. Speed, Skill, Surprise",
		d: "D. Sun, Skill, Surprise",
	},
	assault_cycle: {
		a: "A. Trigger, Escalation, Crisis, Recovery, Post Crisis Depression",
		b: "B. Base, Escalator, Crisis, Recovered, The dip",
		c: "C. Normal, Aroused, Angry, Calming, Depressed",
		d: "D. Baseline, Escalation, Crisis, Recovered, Post Crisis Depression",
	},
	pbs: {
		a: "A. Post, Brexit, Support",
		b: "B. Positive, Bad, Sound",
		c: "C. Positive, Behaviour, Support",
		d: "D. Positive, Behaviour, Solemn",
	},
	violence_aggression_staged_approach: {
		a: "A. Chair De-escalation, Supine, Stage Two Standing Hold",
		b: "B. Prone, Chair De-escalation, Finger and Thumb",
		c: "C. Clenched Fist, Supine, Chair De-escalation",
		d: "D. Thumb in Palm, Chair De-escalation, Supine"
	},
	legal_documents_restraint: {
		a: "A. Health & Safety At Work Act 1974",
		b: "B. Climate Change Act 2008",
		c: "C. Road Safety Act 2006",
		d: "D. Criminal Law Act 1967",
		e: "E. Human Rights Act 1998",
		f: "F. Housing Act 2004",
		g: "G. Mental Health Act 1983"
	},
	duty_of_care: {
		a: "A. A legal rule requiring businesses to guarantee service user satisfaction in circumstances.",
		b: "B. A moral guideline suggesting that staff should try to help others when convenient.",
		c: "C. A legal obligation requiring a person or organisation to take reasonable care to avoid causing harm to themselves and others.",
		d: "D. A contract based duty that only applies when two parties have signed a written agreement."
	},
	mental_health_impact_factors: {
		a: "A. Height",
		b: "B. Size",
		c: "C. Pollution",
		d: "D. Gender",
		e: "E. Weapons",
		f: "F. High Self Esteem",
		g: "G. Mental Health"
	},
	communication: {
		a: "A. 55% words, 38% tone of voice, 7% body language",
		b: "B. 7% words, 38% tone of voice, 55% body language",
		c: "C. 38% words, 55% tone of voice, 7 % body language",
		d: "D. 93% words, 5% tone of voice, 2% body language"
	},
	anger: {
		a: "A. Anger is a feeling or an emotion",
		b: "B. Anger always leads to violence",
		c: "C. Anger is a positive emotion that improves decision making in all situations",
		d: "D. Anger is a behaviour that is only shown through physical aggression"
	},
	aggression: {
		a: "A. A strong feeling of annoyance or frustration that remains internal.",
		b: "B. A mental health disorder that always involves loss of control.",
		c: "C. An act or a behaviour usually intended to cause harm, intimidate, or dominate another person, either physically or verbally.",
		d: "D. A natural emotion experienced when someone feels treated unfairly."
	},
	violence: {
		a: "A. Violence is subjective it’s how it impacts on the individual",
		b: "B. A legal term only used in criminal court, not in everyday situations",
		c: "C. Any feeling of anger or frustration, whether expressed or not",
		d: "D. Aggression that is always accidental and unintentional"
	},
	diversity_factors: {
		a: "A. Characteristics that make all people in a group exactly the same.",
		b: "B. Personal and social attributes, such as age, gender, ethnicity, religion, disability and sexual orientation, that contribute to individual difference.",
		c: "C. Only differences in cultural background, ignoring other personal traits.",
		d: "D. Factors that determine a person’s income level and education exclusively."
	},
	positional_asphyxia: {
		a: "A. Positional asphyxia occurs when body positioning interferes with effective breathing potentially leading to hypoxia and cardiac arrest.  Major risk factors include prone positioning, compression of the torso, obesity, intoxication, extreme agitation and prolonged restraint.  Best practice involves minimising time in high-risk positions, avoiding pressure on the chest/abdomen/neck, actively monitoring breathing and responsiveness, repositioning promptly when safe, and ensuring coordinated team practice to maintain safety for all involved.",
		b: "B. Positional asphyxia occurs when a person intentionally holds their breath during restraint. Risk factors include verbal resistance and refusal to comply. These can be managed by applying firm pressure to the upper back and maintaining the prone position until resistance stops.",
		c: "C. Positional asphyxia is a psychological reaction to being restrained. Risk factors include anxiety and shouting. These can be addressed by increasing the number of staff applying body weight to prevent further movement.",
		d: "D. Positional asphyxia is loss of consciousness caused only be neck compression. Risk factors include standing restraint and handcuff use. These can be managed by securing the individual face-up with weight applied across the chest to prevent movement."
	},
};

const correctAnswers = {
	reasonable_force: "d",
	head_person_role: "c",
	breakaway: "c",
	assault_cycle: "a",
	pbs: "c",
	violence_aggression_staged_approach: "d",
	legal_documents_restraint: ["a", "d", "e", "g"],
	duty_of_care: "c",
	mental_health_impact_factors: ["a", "b", "d", "e", "g"],
	communication: "b",
	anger: "a",
	aggression: "c",
	violence: "a",
	diversity_factors: "b",
	positional_asphyxia: "a"
};

const WrittenAssessmentPage = () => {
	const { state, dispatch, api } = useAppContext();
	useDocumentTitle("PMVA Assessment");
	const { studentId } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [marked, setMarked] = useState(false);
	const [marking, setMarking] = useState(false);
	const [markedError, setMarkedError] = useState(null);
	const [markedSuccess, setMarkedSuccess] = useState(null);
	const [student, setStudent] = useState(null);
	const [course, setCourse] = useState(null);
	const [assessment, setAssessment] = useState(null);
	const isReadyOnly = useUserIsReadOnly();

	useEffect(() => {
		init();
	}, []);

	function init() {
		let course = api.getCurrentCourse();
		let student = api.findStudentOnCourseById(studentId);

		if (course && student) {
			setCourse(course);
			setStudent(student);
			setAssessment(student?.written_assessment);
			setLoading(false);
		}
	}

	function findAnswer(answer_key) {
		if (assessment && Array.isArray(assessment.answers)) {
			// Find answer object by 'question' value
			let answer = assessment.answers.find((a) => {
				return a.question == answer_key;
			});

			// Return the answer object
			return answer;
		}

		return null;
	}

	function handleScoreChange(answer, score) {
		setAssessment(prevAssessment => {
			// Deep clone assessment
			let answers = JSON.parse(JSON.stringify(prevAssessment.answers));

			// Find index of answer in answers array
			let index = answers.findIndex((a) => {
				return a.id == answer.id;
			});

			// Update score
			answers[index].score = score;

			// Return updated assessment
			return {
				...prevAssessment,
				answers: answers,
			};
		});
	}

	async function handleSubmit(e) {
		e.preventDefault();
		setMarkedError(null);
		setMarkedSuccess(null);
		setMarking(true);

		if (assessment) {
			const answers = assessment.answers.map((answer) => {
				return {
					id: answer.id,
					score: parseInt(answer.score),
				};
			});
			const dataToPost = {
				answers: answers,
				assessment_id: assessment.id,
			};

			let { data, error } = await api.fetchData(
				`/written-assessments/${assessment.id}/mark`,
				"POST",
				dataToPost
			);

			console.log(dataToPost)

			if (data?.success) {
				setMarkedSuccess(true);
				setMarked(true);
				setMarking(false);
				dispatch({
					type: "SET_WRITTEN_ASSESSMENT_FOR_STUDENT",
					payload: {
						course_id: course.id,
						student_id: student.id,
						assessment: assessment,
					}
				});
			} else {
				setMarkedError(error || data?.error || "An error occurred");
				setMarking(false);
			}
		}
	}

	const totalScore = assessment?.answers?.reduce((acc, curr) => acc + (parseInt(curr.score) || 0), 0) || 0;
	const PASS_MARK = 40;
	const hasPassed = totalScore >= PASS_MARK;

	async function handleReopen() {
		if (window.confirm("Are you sure you want to reopen this test? This will allow the participant to edit their answers again.")) {
			setMarking(true);
			setMarkedError(null);
			setMarkedSuccess(null);
			let { data, error } = await api.fetchData(
				`/written-assessments/${assessment.id}/reopen`,
				"POST"
			);

			if (data?.success) {
				setMarked(false);
				setMarking(false);
				// Update local assessment state to reflect it's open
				const updatedAssessment = {
					...assessment,
					tutor_marked_at: null,
					tutor_marked_by: 0,
					pass: 0
				};
				setAssessment(updatedAssessment);
				dispatch({
					type: "SET_WRITTEN_ASSESSMENT_FOR_STUDENT",
					payload: {
						course_id: course.id,
						student_id: student.id,
						assessment: updatedAssessment,
					}
				});
				setMarkedSuccess("Test Reopened");
			} else {
				setMarkedError(error || data?.error || "An error occurred");
				setMarking(false);
			}
		}
	}

	function buttonText() {
		if (marking) {
			return "Marking...";
		}

		return "Mark Assessment";
	}

	return (
		<div className="written-assessment-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title={`PMVA Assessment`} />
					<div className="written-assessment">
						<NarrowContentWrapper>
							{!student ? (
								<TableLoading rows={5} />
							) : (
								<>
									<div className="student-info-header text-center mb-4">
										<div className="student-info-header__name h3 mb-0">
											{student.first_name} {student.last_name}
										</div>
										<div className="student-info-header__email text-muted">
											{student.email}
										</div>
									</div>
									<div className="written-assessment__content">
										{assessment ? (
											<form onSubmit={handleSubmit}>
												<AssessmentContent.Provider
													value={{
														assessment,
														handleScoreChange,
													}}>
													<div className="view-form">
														<MultipleChoiceAnswer
															label="1. When discussing reasonable force, what does the acronym PRN stand for?"
															questionId="reasonable_force"
															answer={findAnswer(
																"reasonable_force"
															)}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="2. What is the role of the head person?"
															questionId="head_person_role"
															answer={findAnswer(
																"head_person_role"
															)}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="3. For a Breakaway to be effective, what three major elements are required?"
															questionId="breakaway"
															answer={findAnswer("breakaway")}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="4. Select the five stages of the Assault Cycle."
															questionId="assault_cycle"
															answer={findAnswer(
																"assault_cycle"
															)}
															scoreMax={5}
														/>
														<MultipleChoiceAnswer
															label="5. What do the initials PBS stand for?"
															questionId="pbs"
															answer={findAnswer("pbs")}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="6. We try to employ a staged approach to managing violence and aggression. Please select the correct order starting with low level to high level."
															questionId="violence_aggression_staged_approach"
															answer={findAnswer(
																"violence_aggression_staged_approach"
															)}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="7. From the list below select FOUR legal documents that are relevant to the issues involved in the use of restraint."
															questionId="legal_documents_restraint"
															answer={findAnswer(
																"legal_documents_restraint"
															)}
															scoreMax={4}
														/>
														<MultipleChoiceAnswer
															label="8. Which term best describes Duty of Care?"
															questionId="duty_of_care"
															answer={findAnswer("duty_of_care")}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="9. From the list below, select FIVE impact factors that you may come across when working with patients in a health and social care setting."
															questionId="mental_health_impact_factors"
															answer={findAnswer(
																"mental_health_impact_factors"
															)}
															scoreMax={5}
														/>
														<MultipleChoiceAnswer
															label="10. According to Albert Mehrabian’s research, what proportion of meaning is conveyed through words, tone of voice, and body language when communicating feelings or attitudes?"
															questionId="communication"
															answer={findAnswer(
																"communication"
															)}
															scoreMax={3}
														/>
														<MultipleChoiceAnswer
															label="11. What best describes anger?"
															questionId="anger"
															answer={findAnswer("anger")}
															scoreMax={2}
														/>
														<MultipleChoiceAnswer
															label="12. What best describes aggression?"
															questionId="aggression"
															answer={findAnswer("aggression")}
															scoreMax={2}
														/>
														<MultipleChoiceAnswer
															label="13. What best describes violence?"
															questionId="violence"
															answer={findAnswer("violence")}
															scoreMax={2}
														/>
														<MultipleChoiceAnswer
															label="14. Which of the following best describes diversity factors?"
															questionId="diversity_factors"
															answer={findAnswer(
																"diversity_factors"
															)}
															scoreMax={4}
														/>
														<MultipleChoiceAnswer
															label="15. Which of the following statements BEST defines positional asphyxia and correctly identifies its main risk factors and appropriate risk reduction strategies during a physical restraint, while maintaining safety for both the service user and the team?"
															questionId="positional_asphyxia"
															answer={findAnswer(
																"positional_asphyxia"
															)}
															scoreMax={5}
														/>

														<div className="assessment-results card p-3 mb-4 bg-light">
															<div className="row align-items-center">
																<div className="col">
																	<div className="h4 mb-0">
																		Total Score: <strong>{totalScore} / 50</strong>
																	</div>
																	<div className={`h5 mb-0 ${hasPassed ? 'text-success' : 'text-danger'}`}>
																		Status: <strong>{hasPassed ? 'PASS' : 'FAIL'}</strong> (Pass: {PASS_MARK})
																	</div>
																</div>
																<div className="col-auto">
																	{assessment?.tutor_marked_at && (
																		<Button
																			icon="undo"
																			type="tertiary"
																			text="Reopen Test"
																			onClick={handleReopen}
																			disabled={marking || isReadyOnly}
																		/>
																	)}
																</div>
															</div>
														</div>

														{markedError && (
															<div className="mb-3">
																<AlertMessage
																	type="danger"
																	message={markedError}
																/>
															</div>
														)}
														{markedSuccess && (
															<div className="mb-3">
																<AlertMessage
																	type="success"
																	message={typeof markedSuccess === 'string' ? markedSuccess : "Assessment has been marked"}
																/>
															</div>
														)}
														{!assessment?.tutor_marked_at && (
															<Button
																icon="check"
																type="primary"
																text={buttonText()}
																buttonType="submit"
																disabled={marking || isReadyOnly}
															/>
														)}
													</div>
												</AssessmentContent.Provider>
											</form>
										) : (
											<div>No Assessment Found</div>
										)}
									</div>
								</>
							)}
						</NarrowContentWrapper>
					</div>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

const MultipleAnswerWrapper = ({ label, children }) => {
	return (
		<div className="view-form__form-answer view-form__multiple-answer-wrapper">
			<div className="view-form__question">{label}</div>
			<div className="view-form__answer">{children}</div>
		</div>
	);
};

const Answer = ({ label, answer, scoreMax, multiline = false }) => {
	return (
		<div className="view-form__form-answer">
			{label && <div className="view-form__question">{label}</div>}
			<div className="view-form__answer">{answer?.answer ?? "-"}</div>
			<AnswerScoreInput scoreMax={scoreMax} answer={answer} />
		</div>
	);
};

const MultipleChoiceAnswer = ({ label, questionId, answer, scoreMax }) => {
	const options = multipleChoiceQuestions[questionId];

	function getAnswerClass(option) {
		if (!answer) return "";

		let studentAnswers = answer.answer;
		if (typeof studentAnswers === 'string' && studentAnswers.startsWith('[')) {
			try {
				studentAnswers = JSON.parse(studentAnswers);
			} catch (e) {
				studentAnswers = [];
			}
		}

		let isCorrectArr = correctAnswers[questionId];
		let isMultipleAnswer = Array.isArray(isCorrectArr);

		if (isCorrectArr) {
			if (isMultipleAnswer) {
				let isStudentSelected = Array.isArray(studentAnswers) ? studentAnswers.includes(option) : studentAnswers === option;
				let isOptionCorrect = isCorrectArr.includes(option);

				if (isOptionCorrect && isStudentSelected) return "view-form__answer-option--correct";
				if (!isOptionCorrect && isStudentSelected) return "view-form__answer-option--incorrect";
			} else {
				if (answer.answer == isCorrectArr && answer.answer == option) {
					return "view-form__answer-option--correct";
				}
				if (answer.answer == option) {
					return "view-form__answer-option--incorrect";
				}
			}
		}

		if (Array.isArray(studentAnswers)) {
			if (studentAnswers.includes(option)) return "view-form__answer-option--selected";
		} else {
			if (answer.answer == option) return "view-form__answer-option--selected";
		}

		return "";
	}

	return (
		<div className="view-form__form-answer">
			<div className="view-form__question">{label}</div>
			<div className="view-form__answer">
				{Object.keys(options).map((option, index) => {
					return (
						<div
							className={`view-form__answer-option ${getAnswerClass(
								option
							)}`}
							key={index}>
							{options[option]}
						</div>
					);
				})}
			</div>
			{answer ? (
				<AnswerScoreInput scoreMax={scoreMax} answer={answer} showMinMaxValuesOnly={!Array.isArray(correctAnswers[questionId])} />
			) : (
				<div className="score-input text-muted">No answer provided</div>
			)}
		</div>
	);
};

const AnswerScoreInput = ({ answer, scoreMax, showMinMaxValuesOnly = false }) => {
	const [score, setScore] = useState(answer?.score || 0);
	const { handleScoreChange } = useContext(AssessmentContent);

	useEffect(() => {
		setScoreForAnswer();
	}, []);

	function handleChange(e) {
		let value = e.target.value;
		setScore(value);
		handleScoreChange(answer, value);
	}

	// Automatically assign score based on answer
	function setScoreForAnswer() {
		let value = 0;
		// Answers to intercept
		const interceptAnswers = Object.keys(correctAnswers);

		if (interceptAnswers.includes(answer?.question)) {
			let isCorrectArr = correctAnswers[answer.question];
			let isMultipleAnswer = Array.isArray(isCorrectArr);

			if (isMultipleAnswer) {
				let studentAnswers = answer?.answer;
				if (typeof studentAnswers === 'string' && studentAnswers.startsWith('[')) {
					try {
						studentAnswers = JSON.parse(studentAnswers);
					} catch (e) {
						studentAnswers = [];
					}
				}
				if (!Array.isArray(studentAnswers)) {
					studentAnswers = [studentAnswers].filter(Boolean);
				}

				let correctCount = 0;
				for (const opt of studentAnswers) {
					if (isCorrectArr.includes(opt)) {
						correctCount++;
					}
				}
				value = Math.max(0, Math.min(scoreMax, correctCount));
			} else {
				value = answer?.answer == correctAnswers[answer?.question] ? scoreMax : 0;
			}
			setScore(value);
			handleScoreChange(answer, value);
		}
	}

	return (
		<div className="score-input">
			<div className="score-input__label">Score</div>
			<select
				className="score-input__input"
				value={score}
				onChange={handleChange}
				disabled={showMinMaxValuesOnly}
			>
				{showMinMaxValuesOnly ? (
					<>
						<option value={0}>0</option>
						<option value={scoreMax}>{scoreMax}</option>
					</>
				) : (
					Array.from({ length: scoreMax + 1 }, (_, i) => (
						<option key={i} value={i}>
							{i}
						</option>
					))
				)}
			</select>
			<div className="score-input__max">/ {scoreMax}</div>
		</div>
	);
};

export default WrittenAssessmentPage;
